import { useState, useEffect, useRef, useCallback } from 'react';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { Haptics, NotificationType } from '@capacitor/haptics';

// Generic WebSocket message type - can be extended by specific message types
export interface WebSocketMessage<T = any> {
  type: string;
  data?: T;
  document_id?: number;
  new_status?: string;
  timestamp?: string;
}

export interface WebSocketOptions<T = any> {
  maxReconnectAttempts?: number;
  reconnectInterval?: number;
  onMessage?: (message: WebSocketMessage<T>) => void;
  onOpen?: () => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (error: Event) => void;
}

export interface UseWebSocketReturn {
  socket: WebSocket | null;
  lastMessage: WebSocketMessage | null;
  readyState: number;
  error: Event | null;
  sendMessage: (message: string | object) => void;
  disconnect: () => void;
  reconnect: () => void;
  isConnecting: boolean;
  isOpen: boolean;
  isClosing: boolean;
  isClosed: boolean;
}

const useWebSocket = (url: string | null, options: WebSocketOptions = {}): UseWebSocketReturn => {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [readyState, setReadyState] = useState<number>(0); // 0=CONNECTING, 1=OPEN, 2=CLOSING, 3=CLOSED
  const [error, setError] = useState<Event | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef<number>(0);
  const maxReconnectAttempts = options.maxReconnectAttempts || 5;
  const reconnectInterval = options.reconnectInterval || 3000;

  // Request permissions for notifications on mobile
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      LocalNotifications.requestPermissions();
    }
  }, []);

  const triggerNativeNotification = async (message: WebSocketMessage) => {
    if (!Capacitor.isNativePlatform()) return;

    // Only notify for specific events like new document uploads
    if (message.type === 'document_uploaded' || message.type === 'notification') {
      try {
        // Trigger Haptic feedback
        await Haptics.notification({ type: NotificationType.Success });

        // Schedule local notification
        await LocalNotifications.schedule({
          notifications: [
            {
              title: "New Document Received!",
              body: "A customer has uploaded a new document for printing.",
              id: Math.floor(Math.random() * 1000),
              sound: 'beep.wav', // Fallback to default if not found
              actionTypeId: "",
              extra: message.data
            }
          ]
        });
      } catch (e) {
        console.error('Failed to trigger native notification', e);
      }
    }
  };

  const connect = useCallback(() => {
    if (!url) return;

    // Prevent creating multiple connections
    if (socket && (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN)) {
      console.log('WebSocket already connecting or connected, skipping...');
      return;
    }

    try {
      console.log('Creating new WebSocket connection to:', url);
      const ws = new WebSocket(url);
      
      ws.onopen = (event) => {
        console.log('WebSocket connected');
        setReadyState(WebSocket.OPEN);
        setError(null);
        reconnectAttempts.current = 0;
        
        if (options.onOpen) {
          options.onOpen();
        }
        
        // Send ping periodically to keep connection alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          } else {
            clearInterval(pingInterval);
          }
        }, 30000); // Ping every 30 seconds
      };

      ws.onmessage = (event) => {
        try {
          // Ignore pong responses
          if (event.data === 'pong') return;
          
          const message: WebSocketMessage = JSON.parse(event.data);
          setLastMessage(message);
          
          // Trigger Native Android/iOS Alert
          triggerNativeNotification(message);
          
          // Call message handler if provided
          if (options.onMessage) {
            options.onMessage(message);
          }
        } catch (err) {
          console.warn('Failed to parse WebSocket message:', event.data);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setError(error);
        
        if (options.onError) {
          options.onError(error);
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setReadyState(WebSocket.CLOSED);
        setSocket(null);
        
        if (options.onClose) {
          options.onClose(event);
        }
        
        // Attempt to reconnect if not closed intentionally
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current += 1;
          console.log(`Attempting to reconnect... (${reconnectAttempts.current}/${maxReconnectAttempts})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectInterval);
        }
      };

      setSocket(ws);
      setReadyState(WebSocket.CONNECTING);
      
    } catch (err) {
      console.error('Failed to create WebSocket connection:', err);
      setError(err as Event);
    }
  }, [url, maxReconnectAttempts, reconnectInterval, options]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.close(1000, 'Disconnected by user');
    }
  }, [socket]);

  const sendMessage = useCallback((message: string | object) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      const messageString = typeof message === 'string' ? message : JSON.stringify(message);
      socket.send(messageString);
    } else {
      console.warn('WebSocket is not connected');
    }
  }, [socket]);

  useEffect(() => {
    if (url) {
      connect();
    }

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]); // Only reconnect when URL changes, not on every render

  return {
    socket,
    lastMessage,
    readyState,
    error,
    sendMessage,
    disconnect,
    reconnect: connect,
    isConnecting: readyState === WebSocket.CONNECTING,
    isOpen: readyState === WebSocket.OPEN,
    isClosing: readyState === WebSocket.CLOSING,
    isClosed: readyState === WebSocket.CLOSED,
  };
};

export default useWebSocket;
